import { FilterActionsEnum } from '@unifyapps/defs/types/blocks';
import { useTranslation } from '@unifyapps/i18n/client';
import type { ToolbarVariant } from '@unifyapps/table/types';
import { Box } from '@unifyapps/ui/components/Box';
import { Toggle } from '@unifyapps/ui/components/Toggle';
import { Typography } from '@unifyapps/ui/components/Typography';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useMemo } from 'react';
import type { FiltersType } from '../../../filter/types';
import { QuickFilters } from './QuickFilters';

// Need to override these styles for dark mode for filters buttons
const DARK_MODE_BUTTON_STYLES = {
  '--button-brand-secondary-fg_hover': 'var(--text-brand-tertiary)',
  '--button-neutral-secondary-fg': 'var(--text-tertiary)',
} as React.CSSProperties;

export function Filters({
  filter,
  setFilter,
  filterOptions,
  unread,
  setUnread,
  scheme,
  toolbarVariant,
}: {
  filter: string;
  setFilter: Dispatch<SetStateAction<string | undefined>>;
  filterOptions: { id: string; name: string }[];
  unread: boolean;
  setUnread: Dispatch<SetStateAction<boolean>>;
  scheme?: 'light' | 'dark';
  toolbarVariant?: ToolbarVariant;
}) {
  const { t } = useTranslation(['common']);

  const filterState = useMemo<FiltersType>(
    () => ({
      dataQuickFilter: {
        id: filter,
        type: FilterActionsEnum.QuickFilter,
        filterType: FilterActionsEnum.DataFilter,
      },
    }),
    [filter],
  );

  const onToggleUnread = useCallback(() => {
    setUnread((prev) => !prev);
  }, [setUnread]);

  return (
    <Box className="bg-primary pt-lg pb-xl px-xl z-table gap-md sticky top-0 flex flex-row items-center justify-between">
      <QuickFilters
        filter={filter}
        filterOptions={filterOptions}
        filterState={filterState}
        setFilter={setFilter}
        style={scheme === 'dark' ? DARK_MODE_BUTTON_STYLES : undefined}
        toolbarVariant={toolbarVariant}
      />
      <Box className="gap-md flex flex-row">
        <Typography color="text-secondary" variant="text-xs" weight="medium">
          {t('common:Unreads')}
        </Typography>
        <Toggle checked={unread} onChange={onToggleUnread} size="xs" />
      </Box>
    </Box>
  );
}
